/**
 * LacedUpChi — Mobile Sticky Add to Cart
 *
 * Progressive enhancement only. This never owns a cart form and never
 * submits a variant directly. It mirrors the canonical Product Page state
 * and forwards activation to the existing [data-product-submit] control, so
 * Product Page validation and Cart Drawer interception remain authoritative.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var VISIBLE_CLASS = 'is-visible';

  function text(el) {
    return el ? el.textContent.trim().replace(/\s+/g, ' ') : '';
  }

  function isEnhanced(section) {
    return section && section.hasAttribute('data-product-enhanced');
  }

  function initStickyCart(section) {
    if (!section || section.dataset.stickyCartInitialized === 'true') return;

    var sticky = section.querySelector('[data-product-sticky-cart]');
    if (!sticky) return;

    section.dataset.stickyCartInitialized = 'true';

    var stickyButton = sticky.querySelector('[data-product-sticky-submit]');
    var stickyPrice = sticky.querySelector('[data-product-sticky-price]');
    var media = window.matchMedia(MOBILE_QUERY);
    var infoColumn = section.querySelector('.product__info-column');
    var hasReachedInfo = false;
    var destroyed = false;

    function primarySubmit() {
      return section.querySelector('[data-product-submit]');
    }

    function updateVisibility() {
      if (destroyed) return;

      var submit = primarySubmit();
      if (!submit || !media.matches || !isEnhanced(section)) {
        sticky.classList.remove(VISIBLE_CLASS);
        sticky.setAttribute('aria-hidden', 'true');
        return;
      }

      if (infoColumn && infoColumn.getBoundingClientRect().top < window.innerHeight * 0.72) {
        hasReachedInfo = true;
      }

      var rect = submit.getBoundingClientRect();
      var submitVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      var show = hasReachedInfo && !submitVisible;

      sticky.classList.toggle(VISIBLE_CLASS, show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    function sync() {
      if (destroyed) return;

      var submit = primarySubmit();
      var price = section.querySelector('[data-product-price]');
      var form = section.querySelector('[data-product-form-container]');
      var available = !!form && form.dataset.variantAvailable === 'true';
      var enhanced = isEnhanced(section);

      // Mirror the canonical price ELEMENT, not its flattened text.
      // snippets/product-price.liquid wraps its figures in markup that
      // carries meaning: a <s> for the struck-through compare-at, and
      // visually-hidden "Regular price" / "Sale price" / "Price" labels that
      // exist for screen readers only. Assigning textContent here collapsed
      // all of that on the first variant change — the hidden labels became
      // visible text and a sale showed two undifferentiated prices side by
      // side. Cloning the node keeps the Liquid-rendered markup intact and
      // still involves no HTML parsing and no client-side money maths.
      var priceSource = price && price.querySelector('.price');
      if (stickyPrice && priceSource) {
        var nextPrice = text(priceSource);
        if (stickyPrice.dataset.stickyPriceKey !== nextPrice) {
          stickyPrice.dataset.stickyPriceKey = nextPrice;
          stickyPrice.textContent = '';
          stickyPrice.appendChild(priceSource.cloneNode(true));
        }
      }

      if (stickyButton && submit) {
        var nextLabel = text(submit);
        if (text(stickyButton) !== nextLabel) stickyButton.textContent = nextLabel;

        var shouldDisable = submit.disabled || !available || !enhanced;
        if (stickyButton.disabled !== shouldDisable) stickyButton.disabled = shouldDisable;

        stickyButton.classList.toggle(
          'button--loading',
          submit.classList.contains('button--loading')
        );
      }

      updateVisibility();
    }

    if (stickyButton) {
      stickyButton.addEventListener('click', function () {
        var submit = primarySubmit();
        if (!submit || submit.disabled) return;
        submit.click();
      });
    }

    var observer = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) {
        return !sticky.contains(mutation.target);
      });
      if (relevant) sync();
    });

    observer.observe(section, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'data-variant-available', 'data-product-enhanced']
    });

    var ticking = false;
    function onViewportChange() {
      if (ticking || destroyed) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        updateVisibility();
      });
    }

    window.addEventListener('scroll', onViewportChange, { passive: true });
    window.addEventListener('resize', onViewportChange, { passive: true });

    function onMediaChange() {
      updateVisibility();
    }

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onMediaChange);
    } else if (typeof media.addListener === 'function') {
      media.addListener(onMediaChange);
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      observer.disconnect();
      window.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', onMediaChange);
      } else if (typeof media.removeListener === 'function') {
        media.removeListener(onMediaChange);
      }
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    }

    function onSectionUnload(event) {
      if (event.target && event.target.contains && event.target.contains(section)) destroy();
    }

    document.addEventListener('shopify:section:unload', onSectionUnload);
    sync();
  }

  window.theme = window.theme || {};
  if (window.theme.onSectionLoad) {
    window.theme.onSectionLoad('[data-product-section]', initStickyCart);
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      var sections = document.querySelectorAll('[data-product-section]');
      Array.prototype.forEach.call(sections, initStickyCart);
    });
  }
})();
